import { Box, createStyles, Divider, Grid, makeStyles } from '@material-ui/core'
import React from 'react'
import {
  DateField,
  EditButton,
  FunctionField,
  ReferenceField,
  sanitizeFetchType,
  Show,
  ShowProps,
  Tab,
  TabbedShowLayout,
  TextField,
  TitleProps,
  useQuery,
  useTranslate,
} from 'react-admin'
import { Authority } from '../../../core/auth/Authority'
import { ResourceName } from '../../../core/ResourceName'
import { useHasAuthority } from '../../hooks/useHasAuthority'
import { DeviceStatusesTab } from '../common/modules/ModuleStatus'
import { CameraDto } from '../../../core/dto/device/camera/cameras/camera.dto'
import { DeviceCategoryExtendedDto } from '../../../core/dto/device-category/device-category-extended.dto'
import { DeviceTypes } from '../../../core/enum/DeviceTypes'
import { BarrierDto } from '../../../core/dto/device/barrier/barrier.dto'
import { BarrierSetFreePassButton } from './barrier-show/BarrierSetFreePassButton'
import { BarrierSetPowerButton } from './barrier-show/BarrierSetPowerButton'
import { BarrierSetPPOZButton } from './barrier-show/BarrierSetPPOZButton'
import { BarrierForceReportStateButton } from './barrier-show/BarrierForceReportStateButton'
import { BarrierResetModulesButton } from './barrier-show/BarrierResetModulesButton'
import { ButtonWithRecurringJob } from '../../common/ButtonWithRecurringJob'
import { BlankActions } from '../../common/BlankActions'

const useStyles = makeStyles(() =>
  createStyles({
    generalActions: {
      margin: '2px',
    },
  }),
)

interface BarrierTitleProps extends TitleProps {
  record?: BarrierDto
}

const ShowTitle = (data: BarrierTitleProps) => {
  const { record: barrier } = data
  const translate = useTranslate()
  return (
    <span>
      {translate('resources.barriers.titles.show')}: {barrier?.id}.{' '}
      {barrier?.name}
    </span>
  )
}

const GeneralActions = ({ ...props }) => {
  const { record: barrier } = props
  const hasAuthority = useHasAuthority()
  const classes = useStyles()

  return (
    <>
      <Divider />
      <Box p={1} display="block" textAlign="right">
        <Grid container direction="row" justify="flex-end" alignItems="center">
          <Grid item>
            <EditButton
              className={classes.generalActions}
              basePath="/barriers"
              record={barrier}
              variant="contained"
              disabled={!hasAuthority(Authority.EDIT_BARRIERS)}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

const SetStateActions = ({ ...props }) => {
  const { disabled } = props

  return (
    <>
      <Box p={1} display="block" textAlign="right">
        <Grid container direction="row" justify="flex-end" alignItems="center">
          <Grid item style={{ margin: '2px' }}>
            <ButtonWithRecurringJob
              mainAction={<BarrierSetFreePassButton disabled={disabled} />}
              recurringAction={
                <BarrierSetFreePassButton asRecurringJob disabled={disabled} />
              }
            />
          </Grid>
          <Grid item style={{ margin: '2px' }}>
            <ButtonWithRecurringJob
              mainAction={<BarrierSetPowerButton disabled={disabled} />}
              recurringAction={
                <BarrierSetPowerButton asRecurringJob disabled={disabled} />
              }
            />
          </Grid>
          <Grid item style={{ margin: '2px' }}>
            <ButtonWithRecurringJob
              mainAction={<BarrierSetPPOZButton disabled={disabled} />}
              recurringAction={
                <BarrierSetPPOZButton asRecurringJob disabled={disabled} />
              }
            />
          </Grid>
          <Grid item>
            <BarrierForceReportStateButton />
          </Grid>
          <Grid item>
            <BarrierResetModulesButton />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export const BarrierTabs = ({ ...props }) => {
  const { simpleView, createdAt, ...rest } = props
  const { record: barrier } = rest
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()

  const { data: serverDate } = useQuery(
    {
      type: sanitizeFetchType('getServerDateTime'),
      resource: ResourceName.CURRENT_USER,
      payload: {},
    },
    {
      enabled: !simpleView && !createdAt,
    },
  )

  return (
    <TabbedShowLayout {...rest} syncWithLocation={!simpleView}>
      <Tab label="resources.barriers.tabs.general">
        <TextField source="id" label="ID" />
        <TextField source="name" />
        <FunctionField<BarrierDto>
          source="isWatched"
          render={(record?: BarrierDto) => (
            <div>
              {record?.isWatched
                ? translate('const.yes')
                : translate('const.no')}
            </div>
          )}
        />
        <DateField
          source="lastRefresh"
          showTime
          options={{
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }}
        />
        {barrier?.categoryId && hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
          <ReferenceField
            source="categoryId"
            reference={ResourceName.DEVICE_CATEGORIES}
            link="show"
          >
            <FunctionField<DeviceCategoryExtendedDto>
              source="hierarchyString"
              render={(record?: DeviceCategoryExtendedDto) =>
                `${record?.id}. ${record?.hierarchyString}`
              }
            />
          </ReferenceField>
        )}
        {barrier?.cameraId && hasAuthority(Authority.VIEW_CAMERAS) && (
          <ReferenceField
            source="cameraId"
            reference={ResourceName.CAMERAS}
            link="show"
          >
            <FunctionField<CameraDto>
              source="name"
              render={(camera?: CameraDto) =>
                `${camera?.id}. ${camera?.description}`
              }
            />
          </ReferenceField>
        )}
        {barrier?.tsCanId && hasAuthority(Authority.VIEW_TS_CANS) && (
          <ReferenceField
            source="tsCanId"
            reference={ResourceName.TS_CANS}
            link="show"
          >
            <TextField source="name" />
          </ReferenceField>
        )}
        {(!simpleView && <GeneralActions record={barrier} />) || (
          <BlankActions />
        )}
      </Tab>
      <Tab label="resources.barriers.tabs.statuses" path="statuses">
        {barrier?.jsonStatus && (
          <DeviceStatusesTab
            jsonStatus={barrier.jsonStatus}
            reversedDirection={barrier?.reversedDirection}
            deviceType={DeviceTypes.BARRIERS}
            referenceDateTime={createdAt || serverDate?.serverDateTime}
          />
        )}
        {(!simpleView && <SetStateActions />) || <BlankActions />}
      </Tab>
    </TabbedShowLayout>
  )
}

export const BarrierShow = (props: ShowProps) => (
  <Show {...props} actions={false} title={<ShowTitle />}>
    <BarrierTabs />
  </Show>
)
